import { defineComponent as _defineComponent } from 'vue'
import { reactive, watch, ref, onMounted, computed, PropType } from 'vue';
import { ValidatedForm } from 'ah-common-lib/src/form/components';
import { makeFormModel, toDataModel, setState, getChildModel } from 'ah-common-lib/src/form/helpers';
import { otpField } from 'ah-common-lib/src/form/models';
import { FormValidation, FormEvent } from 'ah-common-lib/src/form/interfaces';
import { AuthErrorCodes, MFAType, SecurityErrorCodes } from 'ah-api-gateways';
import { useAuthStore } from 'bd-common/src/store/authStore';
import { ExpiryTime } from 'ah-api-gateways/models/expiry';
import { useServices } from 'bd-common/src/services';
import { useConfig } from '../../config';
import { VButton } from 'ah-common-lib/src/common/components';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import ToastComponent from '../common/ToastComponent.vue';
import { useToast } from 'ah-common-lib/src/toast';
import { ToastType } from 'ah-common-lib/src/toast/toastInterfaces';
import { DEFAULT_OTP_EXPIRY_TIME } from 'ah-common-lib/src/helpers/otp';

enum OtpCodeError {
  INVALID_OTP = 'INVALID_OTP',
  EXPIRED_OTP = 'EXPIRED_OTP',
}


export default /*#__PURE__*/_defineComponent({
  __name: 'OtpForm',
  props: {
  otpTarget: {
    type: String,
    default: '',
  },
  otpType: {
    type: String as PropType<MFAType>,
    required: true,
  },
  expiry: {
    type: Object as PropType<ExpiryTime>,
    required: false,
  },
},
  emits: ["error", "success", "cancel"],
  setup(__props, { emit }: { emit: ({
  (e: 'error', value: any): void;
  (e: 'success'): void;
  (e: 'cancel'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;





const state = reactive({
  otpFM: makeFormModel({
    name: 'otpForm',
    title: 'OTP',
    fieldWrapperClass: 'otp-field-wrapper',
    fieldType: 'form',
    fields: [otpField('otp', '', { hideErrors: true })],
  }),
  otpFV: null as FormValidation | null,
  expiredOtp: false,
});

const otpCodeError = ref<OtpCodeError | null>(null);

const requestManager = useRequestManager({ exposeToParent: false }).manager;

const authStore = useAuthStore();

const services = useServices();

const toast = useToast();

const config = useConfig();

const form = ref<ValidatedForm | null>(null);

const isResendCodeButtonDisabled = ref(false);

const expiryInner = ref<ExpiryTime>();

watch(
  () => props.expiry,
  () => {
    expiryInner.value = props.expiry;
  },
  { immediate: true }
);

const minutesToExpire = computed(() => {
  if (expiryInner.value?.expiresIn) {
    return Math.floor(expiryInner.value.expiresIn / 60);
  }
  return DEFAULT_OTP_EXPIRY_TIME;
});

watch(
  () => state.expiredOtp,
  () => {
    state.otpFM.otp = [];
    const otpField = getChildModel(state.otpFM, 'otp');
    if (otpField) {
      setState(otpField, 'readonly', state.expiredOtp);
    }
  }
);

function otpContinue() {
  if (state.otpFV?.$invalid) {
    return;
  }
  otp();
}

function onFormEvent(event: FormEvent) {
  if (event.event === 'form-field-submit') {
    otpContinue();
  }
}

function otp() {
  requestManager.currentOrNewPromise('sendOtp', () =>
    authStore.otp(toDataModel(state.otpFM).otp).then(
      () => onOtpSuccess(),
      (error: any) => {
        if (error.response) {
          if (error.response?.data?.code === AuthErrorCodes.OTP_ATTEMPTS_EXCEEDED) {
            toast.error(error.response?.data?.message);
            backToLogin();
          } else if (error.response?.data?.code === SecurityErrorCodes.INVALID_OTP) {
            otpCodeError.value = OtpCodeError.INVALID_OTP;
          } else if (error.response?.data?.code === SecurityErrorCodes.EXPIRED_OTP) {
            otpCodeError.value = OtpCodeError.EXPIRED_OTP;
            state.expiredOtp = true;
          } else {
            toast.error('Oops, there was an error while trying to validate your OTP');
          }
        }
        emit('error', error);
      }
    )
  );
}

function refreshOtp() {
  requestManager.currentOrNew('refreshOtp', services.auth.refreshOtp()).subscribe(
    (res) => {
      state.otpFM.otp = [];
      state.expiredOtp = false;
      toast.success('Two-step authentication code resent');
      expiryInner.value = res;
    },
    (error) => {
      if (error.response) {
        if (error.response?.data?.code === SecurityErrorCodes.INVALID_OTP) {
          otpCodeError.value = OtpCodeError.INVALID_OTP;
        } else if (error.response?.data?.code === SecurityErrorCodes.EXPIRED_OTP) {
          otpCodeError.value = OtpCodeError.EXPIRED_OTP;
          state.expiredOtp = true;
        } else {
          toast.error('Oops, there was an error while trying to validate your OTP');
        }
      }
    }
  );
}

function resendCode() {
  isResendCodeButtonDisabled.value = true;

  refreshOtp();

  setTimeout(() => {
    isResendCodeButtonDisabled.value = false;
  }, 60000);
}

const errorTitle = computed(() => {
  if (otpCodeError.value === OtpCodeError.INVALID_OTP) {
    return 'Wrong code. Please try again.';
  } else if (otpCodeError.value == OtpCodeError.EXPIRED_OTP) {
    return 'Code expired. Please click on "Resend code"';
  }
  return '';
});

const errorMessage = computed(() => {
  if (otpCodeError.value === OtpCodeError.INVALID_OTP) {
    return 'Sorry, the code you entered is incorrect.';
  } else if (otpCodeError.value == OtpCodeError.EXPIRED_OTP) {
    return 'Sorry, the code you entered has expired.';
  }
  return '';
});

function closeOtpCodeError() {
  otpCodeError.value = null;
}

function onOtpSuccess() {
  emit('success');
}

/**
 * called when it is needed to go back and change the email; emits cancel
 */
function backToLogin() {
  emit('cancel');
}

onMounted(() => {
  form.value?.form.triggerFieldAction('otpForm.otp', 'focus');
});

return { __sfc: true,props, emit, state, OtpCodeError, otpCodeError, requestManager, authStore, services, toast, config, form, isResendCodeButtonDisabled, expiryInner, minutesToExpire, otpContinue, onFormEvent, otp, refreshOtp, resendCode, errorTitle, errorMessage, closeOtpCodeError, onOtpSuccess, backToLogin, ValidatedForm, MFAType, VButton, ToastComponent, ToastType }
}

})